import type { Variable } from "../types";
import type { VariableType } from "../calculated-field/CalculatedFieldOperation/types";

export type ExpedienteSource = "expediente";

export type ActionItemType =
  | "read"
  | "create"
  | "update"
  | "deactivate"
  | "link_users";

export type LinkSubActionType = "link_users" | "link_expedients";

export type RegistryInputMode = "inp" | "var";

export type ValueInputType =
  | "string"
  | "number"
  | "date"
  | "boolean"
  | "variable"
  | "regex"
  | "positive-integer";

/**
 * Type of expiration configuration
 * - expiration_date: A specific date when the value expires
 * - expiration_time: A relative time period (e.g., 5 years from creation)
 */
export type ExpirationType = "expiration_date" | "expiration_time";

/**
 * Time unit for expiration timer
 */
export type ExpirationTimeUnit =
  | "hours"
  | "days"
  | "weeks"
  | "months"
  | "years";

/**
 * Configuration for field value expiration
 */
export interface FieldExpirationConfig {
  /** Type of expiration: specific date or time period */
  type: ExpirationType;
  /** Specific expiration date (ISO string or Variable) - used when type is 'expiration_date' */
  expirationDate?: string | Variable;
  /** Input type for expiration date */
  expirationDateInputType?: "inp" | "var";
  /** Amount of time until expiration - used when type is 'expiration_time' */
  expirationAmount?: number;
  /** Unit of time for expiration - used when type is 'expiration_time' */
  expirationUnit?: ExpirationTimeUnit;
  /** Notification date (ISO string or Variable) - used when type is 'expiration_date' */
  notificationDate?: string | Variable;
  /** Input type for notification date */
  notificationDateInputType?: "inp" | "var";
  /** Amount of time before expiration to send notification - used when type is 'expiration_time' */
  notificationAmount?: number;
  /** Unit of time for notification - used when type is 'expiration_time' */
  notificationUnit?: ExpirationTimeUnit;
  /** Value to replace the field with when expired (string for 'inp', Variable for 'var') */
  replacementValue: string | Variable;
  /** Input type for replacement value */
  replacementInputType: "inp" | "var";
}

/**
 * Maps ValueInputType to VariableType for filtering variables by type
 * Used for TypeValueInput component filterByType prop
 */
export const mapValueInputTypeToVariableType = (
  valueInputType: ValueInputType,
): VariableType => {
  switch (valueInputType) {
    case "string":
    case "regex":
      return "string";
    case "number":
    case "positive-integer":
      return "number";
    case "date":
      return "date";
    case "boolean":
      return "boolean";
    case "variable":
      return "string"; // fallback to string for generic variable type
    default:
      return "string"; // fallback
  }
};

/**
 * Maps ValueInputType to an array of compatible variable types for filtering
 * Used for DropdownList variable filtering - includes alternative type names
 */
export const getCompatibleVariableTypes = (
  valueInputType: ValueInputType,
): string[] => {
  switch (valueInputType) {
    case "string":
    case "regex":
      return ["string", "str", "text", "file"];
    case "number":
    case "positive-integer":
      return ["number", "num", "integer", "float", "int"];
    case "date":
      return ["date", "datetime"];
    case "boolean":
      return ["boolean", "bool"];
    case "variable":
      return []; // No filter for generic variable type - show all
    default:
      return []; // No filter - show all
  }
};

export interface ValueColumn {
  /** Display label for the field (from entity field name) */
  label: string;
  /** Whether the field is required */
  isRequired: boolean;
  /** Unique identifier for the column (entity field id) */
  name: string;
  /** Placeholder text for the input */
  placeholder: string;
  /** Type of value expected */
  valueType: ValueInputType;
  /** Current value of the field */
  value: string | Variable;
  /** Input mode: direct input or variable selection */
  inputType: "input" | "variable";
  /** Optional expiration configuration for the field value */
  expiration?: FieldExpirationConfig;
  /** Entity field type (original from backend) */
  fieldType?: string;
  /** Options for select/multiselect fields */
  options?: string[];
}

//TODO: merge with if
export type ConditionEnum =
  | "EXIST"
  | "NOT_EXIST"
  | "IS_EMPTY"
  | "IS_NOT_EMPTY"
  | "EQUALS"
  | "NOT_EQUALS"
  | "CONTAINS"
  | "NOT_CONTAINS"
  | "STARTS_WITH"
  | "ENDS_WITH"
  | "MATCHES"
  | "NOT_MATCHES"
  | "GREATER_THAN"
  | "LESS_THAN"
  | "GREATER_THAN_OR_EQUAL"
  | "LESS_THAN_OR_EQUAL"
  | "IS_AFTER"
  | "IS_BEFORE"
  | "IS_IN_OR_AFTER"
  | "IS_IN_OR_BEFORE"
  | "IS_TRUE"
  | "IS_FALSE"
  | "LENGTH_EQUAL"
  | "LENGTH_NOT_EQUAL"
  | "LENGTH_GREATER_THAN"
  | "LENGTH_LESS_THAN"
  | "LENGTH_GREATER_THAN_OR_EQUAL"
  | "LENGTH_LESS_THAN_OR_EQUAL"
  | "NOT_STARTS_WITH"
  | "NOT_ENDS_WITH"
  | "BETWEEN"
  | "ANY"
  | "AFTER"
  | "BEFORE"
  | "AFTER_EQUALS"
  | "BEFORE_EQUALS";

export interface ExpedienteOperation {
  name: ConditionEnum;
  label: string;
  type?: ValueInputType;
  needsComparativeValue?: boolean;
  needsComparativeRange?: boolean;
  comparativeValueType?:
    | "string"
    | "number"
    | "regex"
    | "date"
    | "variable"
    | "boolean"
    | "positive-integer";
}

/**
 * Link action type for associating/disassociating users
 */
export type LinkActionType = "link" | "unlink";

/**
 * Link action type for associating/disassociating expedients (registros)
 */
export type LinkExpedientsActionType = "link" | "unlink";

/**
 * Expedient/Registry entry for linking/unlinking to expediente
 */
export interface LinkExpedientEntry {
  /** Unique ID for this entry in the form (UI-only) */
  id: string;
  /** Expedient document (ID or unique_code) - can be string or Variable */
  expedientDocument?: string | Variable;
  /** Input type for expedient document field */
  inputType?: "inp" | "var";
}

/**
 * User identifier type for linking users
 */
export type UserIdentifierType = "email" | "document" | "id";

/**
 * User entry for linking/unlinking to expediente
 */
export interface LinkUserEntry {
  /** Unique ID for this entry in the form (UI-only) */
  id: string;
  /** Applicant UUID (for existing users) */
  applicantId?: string;
  /** User document/ID value (for new users) - can be string or Variable */
  document?: string | Variable;
  /** Input type for document field */
  documentInputType?: "inp" | "var";
  /** User email value (for new users) - can be string or Variable */
  email?: string | Variable;
  /** Input type for email field */
  emailInputType?: "inp" | "var";
  /** Role ID or Variable to assign (optional, only for linking) */
  roleId?: string | Variable;
  /** Input type for role field */
  roleInputType?: "inp" | "var";
}
